import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { useFilterAndSearchEffect } from "./useFilterAndSearchEffect";
import { useFilterPayload } from "./useFilterPayload";
import { GenericAuthStatusState } from "./useGenericAuthStatusTableData";
import { usePaginationReset } from "./usePaginationReset";
import { useSearchTerm } from "./useSearchTerm";
import { useValidFilters } from "./useValidFilters";

/**
 * Filter and search orchestrator hook
 *
 * Orchestrates all filter and search-related functionality by composing smaller hooks.
 * This pattern keeps the component clean while maintaining flexibility to use individual hooks.
 *
 * Composed hooks:
 * 1. useSearchTerm - Search text state
 * 2. usePaginationReset - Pagination reset counter
 * 3. useValidFilters - Filtered Redux filters
 * 4. useFilterPayload - Generates Algolia payload
 * 5. useFilterAndSearchEffect - Triggers API calls
 *
 * @param {boolean} initialDataFetchingExecuted - Flag if initial fetch is complete
 * @param {Function} setTableData - Table data setter
 * @param {Function} setGenericAuthStatusState - API state setter
 * @param {string} tabId - Auth status tab identifier (e.g., "pending", "hold")
 * @returns {Object} Combined filter and search state
 * @returns {AlgoliaSearchParams | null} returns.filterPayload - Generated Algolia payload
 * @returns {string} returns.searchTerm - Current search text
 * @returns {Function} returns.setSearchTerm - Search term setter
 * @returns {number} returns.paginationResetCount - Pagination reset counter
 * @returns {Function} returns.setPaginationResetCount - Counter setter
 * @returns {FilterValues[]} returns.validFilters - Relevant filters for this table/tab
 *
 * @example
 * const { searchTerm, setSearchTerm, paginationResetCount, validFilters } =
 *   useFilterAndSearch(true, setTableData, setGenericAuthStatusState, "pending");
 */
export const useFilterAndSearch = (
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<
    React.SetStateAction<GenericAuthStatusTableData[]>
  >,
  setGenericAuthStatusState: React.Dispatch<
    React.SetStateAction<GenericAuthStatusState>
  >,
  tabId: string,
) => {
  const { searchTerm, setSearchTerm } = useSearchTerm(tabId);
  const { paginationResetCount, setPaginationResetCount } =
    usePaginationReset();
  const validFilters = useValidFilters(tabId);
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    tabId,
  );

  useFilterAndSearchEffect(
    filterPayload,
    initialDataFetchingExecuted,
    setTableData,
    setGenericAuthStatusState,
    setPaginationResetCount,
    tabId,
  );

  return {
    filterPayload,
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    setPaginationResetCount,
    validFilters,
  };
};
